/**
 * Sites — the buildings inside one customer.
 *
 * The sibling of the Customers list one level down, and it reads as one on purpose: same
 * triage summary, same filter chips, same worst-first ordering. A customer with five
 * buildings is browsable as cards; a hotel group with sixty is not, and the question an
 * operator asks here is the same one they asked a level up -- WHICH of these needs me.
 *
 * Each row carries the site's fleet roll-up from GET /orgs/<id>/sites, using the identical
 * health ladder as /customers (critical if any open critical alert, warn if any open alert
 * or an offline device, else healthy). Two screens answering "what is wrong here" with two
 * different definitions of wrong is how an operator learns to distrust both.
 *
 * The roll-up fields are OPTIONAL on the Site type: other endpoints still hand back a bare
 * site, so every cell tolerates their absence rather than rendering NaN.
 *
 * Pre-register stays a per-row action (it is already a dialog trigger) — reserve devices by
 * MAC so the moment owgw adopts one it lands in THIS site, skipping the Unassigned pool. MAC
 * only, same parser and wording as the Onboard page (lib/claimIdentifiers, UI review
 * 2026-10-17): a vendor serial is stored by the API but can never match. Its click is stopped
 * from bubbling, or opening the dialog would also navigate. The trigger is shown to
 * org_admin+ only: POST/DELETE preregistrations are @require("org_admin") on the server.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import * as Dialog from "@radix-ui/react-dialog";
import { useSession } from "@/stores/session";
import { QUARANTINE_LABEL, isQuarantineOrg } from "@/lib/quarantine";
import {
  useSites,
  useCreateSite,
  usePreregs,
  useAddPreregs,
  useDeletePrereg,
} from "@/lib/api/hooks";
import { DataGrid } from "@/components/data/DataGrid";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { StatusPill } from "@/components/ui/StatusPill";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import { cn } from "@/lib/cn";
import {
  CLAIM_HINT,
  CLAIM_LABEL,
  CLAIM_PLACEHOLDER,
  invalidClaimMessage,
  parseClaimInput,
} from "@/lib/claimIdentifiers";
import type { SiteListRow } from "@/types/domain";

/** Worst first — the order the table opens on. Mirrors the Customers list exactly. */
const HEALTH_RANK: Record<NonNullable<SiteListRow["health"]>, number> = {
  critical: 0, warn: 1, healthy: 2,
};

/** Anything not clean. Tolerates a bare Site with no roll-up (then nothing is known). */
function needsAttention(s: SiteListRow): boolean {
  return (s.health ?? "healthy") !== "healthy"
    || (s.alerts_open ?? 0) > 0
    || (s.devices_offline ?? 0) > 0;
}

export default function Sites() {
  const orgId = useSession((st) => st.currentOrgId);
  const orgName = useSession((st) => st.currentOrg()?.name);
  // The quarantine holding area's sites are placeholders, not customer networks (2026-10-19):
  // say so in the header and do not offer "add a site" to it.
  const quarantine = useSession((st) => isQuarantineOrg(st.currentOrg()));
  const setSite = useSession((st) => st.setSite);
  const navigate = useNavigate();
  const { data, isLoading, error } = useSites();

  const [query, setQuery] = useState("");
  const [onlyAttention, setOnlyAttention] = useState(false);

  const sites = useMemo(() => (data?.items ?? []) as SiteListRow[], [data]);

  const totals = useMemo(() => ({
    count: sites.length,
    attention: sites.filter(needsAttention).length,
    offline: sites.reduce((n, st) => n + (st.devices_offline ?? 0), 0),
    alerts: sites.reduce((n, st) => n + (st.alerts_open ?? 0), 0),
  }), [sites]);

  const rows = useMemo(() => {
    const term = query.trim().toLowerCase();
    return sites
      .filter((st) => {
        if (onlyAttention && !needsAttention(st)) return false;
        if (!term) return true;
        return st.name.toLowerCase().includes(term)
          || (st.location ?? "").toLowerCase().includes(term);
      })
      .slice()
      .sort((a, b) =>
        HEALTH_RANK[a.health ?? "healthy"] - HEALTH_RANK[b.health ?? "healthy"]
        || (b.alerts_open ?? 0) - (a.alerts_open ?? 0)
        || (b.devices_offline ?? 0) - (a.devices_offline ?? 0)
        || a.name.localeCompare(b.name));
  }, [sites, query, onlyAttention]);

  const enter = (site: SiteListRow) => {
    const sid = String(site.id);
    setSite(sid);
    navigate(`/o/${orgId}/s/${sid}/dashboard`);
  };

  const columns = useMemo<ColumnDef<SiteListRow, unknown>[]>(() => [
    {
      id: "site",
      header: "Site",
      accessorFn: (st) => st.name,
      cell: (ctx) => {
        const site = ctx.row.original;
        const health = site.health ?? "healthy";
        return (
          <div className="flex min-w-0 items-center gap-2">
            {/* A severity stripe reads before any number does. */}
            <span
              aria-hidden
              className={cn("h-5 w-1 shrink-0 rounded-full",
                health === "critical" ? "bg-critical"
                  : health === "warn" ? "bg-warn" : "bg-healthy/40")}
            />
            <div className="min-w-0">
              <div className="truncate font-medium text-ink">{site.name}</div>
              <div className="truncate text-xs text-ink-subtle">
                {site.location || "No location set"}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      id: "health",
      header: "Health",
      accessorFn: (st) => HEALTH_RANK[st.health ?? "healthy"],
      cell: (ctx) => <StatusPill status={ctx.row.original.health ?? "healthy"} />,
    },
    {
      id: "devices",
      header: "Devices",
      accessorFn: (st) => st.devices_total ?? 0,
      cell: (ctx) => {
        const site = ctx.row.original;
        // An endpoint that returns a bare Site shows an em dash, never NaN.
        if (site.devices_total == null) return <span className="text-ink-subtle">—</span>;
        return (
          <span className="tabular-nums text-ink">
            {site.devices_online ?? 0}
            <span className="text-ink-subtle">/{site.devices_total}</span>
          </span>
        );
      },
    },
    {
      id: "offline",
      header: "Offline",
      accessorFn: (st) => st.devices_offline ?? 0,
      cell: (ctx) => {
        const n = ctx.row.original.devices_offline ?? 0;
        // Zero is the good case and should recede, not compete for attention.
        return n === 0 ? <span className="text-ink-subtle">—</span>
          : <span className="font-medium tabular-nums text-warn">{n}</span>;
      },
    },
    {
      id: "alerts",
      header: "Alerts",
      accessorFn: (st) => st.alerts_open ?? 0,
      cell: (ctx) => {
        const site = ctx.row.original;
        const n = site.alerts_open ?? 0;
        if (n === 0) return <span className="text-ink-subtle">—</span>;
        return (
          <span className={cn("font-medium tabular-nums",
            site.health === "critical" ? "text-critical" : "text-warn")}>{n}</span>
        );
      },
    },
    {
      id: "actions",
      header: "",
      cell: (ctx) => (
        <RoleGate min="org_admin" mode="hide">
          {/* The row navigates on click; opening this dialog must not also navigate. */}
          <span onClick={(e) => e.stopPropagation()}>
            <PreregManager
              site={{ id: Number(ctx.row.original.id), name: ctx.row.original.name }}
            />
          </span>
        </RoleGate>
      ),
    },
  ], []);

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader
          title="Sites"
          subtitle={
            quarantine
              ? `Placeholder sites in ${QUARANTINE_LABEL}. These are not customer networks; devices waiting to be claimed are on the Overview.`
              : orgName
                ? `Networks in ${orgName}`
                : undefined
          }
        />
        {/* org_admin (OP#1580): POST /orgs/<id>/sites refuses a site_operator. */}
        {!quarantine && (
          <RoleGate min="org_admin" mode="hide">
            <AddSite />
          </RoleGate>
        )}
      </div>

      {error && <ErrorBanner error={error} />}

      {sites.length > 0 && (
        <>
          {/* The one line that answers "is anything wrong" without reading the table.
              A row of zeroes in alert colours would just train people to ignore them. */}
          {totals.attention === 0 ? (
            <p role="status" className="mt-3 text-sm text-ink-muted">
              All {totals.count} site{totals.count === 1 ? "" : "s"} healthy — nothing
              offline, no open alerts.
            </p>
          ) : (
            <p role="status" className="mt-3 text-sm text-ink">
              <span className="font-medium text-warn">{totals.attention}</span>
              <span className="text-ink-muted">
                {totals.attention === 1 ? " site needs" : " sites need"} attention
              </span>
              {totals.offline > 0 && (
                <>
                  <span className="text-ink-subtle"> · </span>
                  <span className="font-medium tabular-nums text-warn">{totals.offline}</span>
                  <span className="text-ink-muted"> devices offline</span>
                </>
              )}
              {totals.alerts > 0 && (
                <>
                  <span className="text-ink-subtle"> · </span>
                  <span className="font-medium tabular-nums text-warn">{totals.alerts}</span>
                  <span className="text-ink-muted"> open alerts</span>
                </>
              )}
            </p>
          )}

          <div className="mb-3 mt-3 flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor="site-search">Search sites</label>
            <input
              id="site-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search sites"
              className="w-full max-w-xs rounded border border-line bg-surface px-3 py-1.5 text-sm text-ink placeholder:text-ink-subtle"
            />
            <button
              type="button"
              aria-pressed={onlyAttention}
              onClick={() => setOnlyAttention((v) => !v)}
              className={cn("rounded-full border px-2.5 py-1 text-xs transition-colors",
                onlyAttention ? "border-brand bg-brand/10 text-brand"
                  : "border-line text-ink-muted hover:bg-surface-2",
                !onlyAttention && totals.attention > 0 && "text-warn")}
            >
              Needs attention {totals.attention}
            </button>
            {rows.length !== sites.length && (
              <span className="text-xs text-ink-muted">
                {rows.length} of {sites.length}
              </span>
            )}
          </div>
        </>
      )}

      {data && sites.length === 0 ? (
        <EmptyState
          title="No sites yet"
          hint="Add a site to start placing devices in this customer."
        />
      ) : (
        <DataGrid
          data={rows}
          columns={columns}
          rowKey={(st) => String(st.id)}
          dense
          isLoading={isLoading}
          onRowActivate={enter}
          initialSorting={[{ id: "health", desc: false }]}
          empty={
            <span>
              {onlyAttention ? "Every site is healthy." : "No site matches that search."}
            </span>
          }
          renderCard={(st) => <SiteCard site={st} />}
        />
      )}
    </div>
  );
}

/** The sub-`lg` card DataGrid reflows to. Same facts, stacked. */
function SiteCard({ site }: { site: SiteListRow }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-medium text-ink">{site.name}</div>
          <div className="mt-0.5 truncate text-xs text-ink-muted">
            {site.location || "No location set"}
          </div>
        </div>
        <StatusPill status={site.health ?? "healthy"} />
      </div>
      <dl className="grid grid-cols-3 gap-2 text-sm">
        <div>
          <dt className="text-xs text-ink-muted">Devices</dt>
          <dd className="tabular-nums text-ink">
            {site.devices_total == null
              ? "—"
              : `${site.devices_online ?? 0}/${site.devices_total}`}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-ink-muted">Offline</dt>
          <dd className={cn("tabular-nums",
            (site.devices_offline ?? 0) > 0 ? "text-warn" : "text-ink")}>
            {site.devices_offline ?? 0}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-ink-muted">Alerts</dt>
          <dd className={cn("tabular-nums",
            (site.alerts_open ?? 0) > 0 ? "text-warn" : "text-ink")}>
            {site.alerts_open ?? 0}
          </dd>
        </div>
      </dl>
      <RoleGate min="org_admin" mode="hide">
        <span onClick={(e) => e.stopPropagation()}>
          <PreregManager site={{ id: Number(site.id), name: site.name }} />
        </span>
      </RoleGate>
    </div>
  );
}

/** "Add site" button + dialog (name + optional location). */
function AddSite() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const create = useCreateSite();

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    create.mutate(
      { name: name.trim(), ...(location.trim() ? { location: location.trim() } : {}) },
      {
        onSuccess: () => {
          setOpen(false);
          setName("");
          setLocation("");
        },
      },
    );
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <Button variant="primary">Add site</Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">Add site</Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            A site (network) groups the devices at one location.
          </Dialog.Description>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Name</span>
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Main Site"
                className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Location (optional)</span>
              <input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="123 Main St, Floor 2"
                className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand"
              />
            </label>
            {create.isError && <span className="text-xs text-critical">Could not create the site.</span>}
            <div className="md-dialog-footer flex items-center justify-end gap-2">
              <Dialog.Close asChild>
                <Button variant="ghost" type="button">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button variant="primary" type="submit" disabled={!name.trim() || create.isPending}>
                {create.isPending ? "Creating…" : "Create"}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * "Pre-register" action + dialog for one site. Paste MAC addresses (comma / space /
 * newline separated); each is reserved to this site so it auto-claims on arrival.
 * Lists existing reservations with per-row remove.
 */
function PreregManager({ site }: { site: { id: number; name: string } }) {
  const [open, setOpen] = useState(false);
  const siteId = String(site.id);
  const { data } = usePreregs(open ? siteId : null); // only fetch once the dialog opens
  const add = useAddPreregs(siteId);
  const del = useDeletePrereg(siteId);
  const [text, setText] = useState("");
  const [note, setNote] = useState("");

  const rows = data?.items ?? [];
  // Same parser as the Onboard claim box: MACs only; anything else blocks Reserve with a reason.
  const parsed = parseClaimInput(text);
  const invalid = invalidClaimMessage(parsed);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const identifiers = parsed.macs;
    if (identifiers.length === 0 || invalid) return;
    add.mutate(
      { identifiers, ...(note.trim() ? { note: note.trim() } : {}) },
      { onSuccess: () => { setText(""); setNote(""); } },
    );
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className="text-xs font-medium text-brand hover:underline"
        >
          Pre-register devices{rows.length > 0 ? ` (${rows.length})` : ""}
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">
            Pre-register — {site.name}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            Reserve devices by MAC address. When one connects, it lands in this site
            automatically instead of the Unassigned pool.
          </Dialog.Description>

          <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">{CLAIM_LABEL}</span>
              <textarea
                autoFocus
                rows={3}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={CLAIM_PLACEHOLDER}
                className="rounded border border-line bg-surface px-3 py-2 font-mono text-xs text-ink outline-none focus:border-brand"
              />
              <span className="text-[0.6875rem] text-ink-subtle">{CLAIM_HINT}</span>
              {invalid && <span className="text-xs text-critical" role="alert">{invalid}</span>}
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Note (optional)</span>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="RMA batch / floor 2 refresh"
                className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand"
              />
            </label>
            {add.isError && <span className="text-xs text-critical">Could not add reservations.</span>}
            <div className="flex items-center justify-end">
              <Button variant="primary" type="submit"
                disabled={parsed.macs.length === 0 || !!invalid || add.isPending}>
                {add.isPending ? "Adding…" : "Reserve"}
              </Button>
            </div>
          </form>

          {/* Existing reservations */}
          <div className="mt-4 border-t border-line pt-3">
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-muted">
              Reserved ({rows.length})
            </div>
            {rows.length === 0 ? (
              <div className="text-sm text-ink-subtle">No devices reserved yet.</div>
            ) : (
              <ul className="flex max-h-56 flex-col gap-1 overflow-y-auto">
                {rows.map((r) => (
                  <li
                    key={r.id}
                    className="flex items-center justify-between gap-2 rounded bg-surface-2 px-2 py-1"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-mono text-xs text-ink">{r.raw || r.identifier}</div>
                      {r.note && <div className="truncate text-xs text-ink-subtle">{r.note}</div>}
                    </div>
                    <button
                      type="button"
                      aria-label={`Remove ${r.raw || r.identifier}`}
                      className="shrink-0 text-xs text-ink-muted hover:text-critical"
                      disabled={del.isPending}
                      onClick={() => del.mutate(r.id)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-4 flex items-center justify-end">
            <Dialog.Close asChild>
              <Button variant="ghost" type="button">Done</Button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
